import React, { Suspense, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Panel } from 'react-bootstrap';
import { navigate } from 'raviger';
import _ from 'lodash';

import withAuthentication from '../../hoc/withAuthentication';
import withLayout from '../../hoc/withLayout';

import { useAlertStore } from '../../store/providers/Alert';
import { useFranchiseFineStore } from '../../store/providers/FranchiseFine';

import Permission from '../../components/Permission/Permission';
import { breadcrumbAddRoute, breadcrumbRemoveRoute } from '../../store/actions/breadcrumb';

import Spinner from '../../components/Spinner/Spinner';

import * as constants from '../../constants';
import { columns, sorting } from './FinesListSchema';

const BaseList = React.lazy(() => import('../../components/Lists/BaseList/BaseList'));
const SideMenu = React.lazy(() => import('../../components/SideMenu/SideMenu'));
const PageHeader = React.lazy(() => import('../../components/PageHeader/PageHeader'));

function Fines() {
    const dispatch = useDispatch();

    const { addAlert } = useAlertStore();
    const { listRecords, data, clearData } = useFranchiseFineStore();

    const [isMounted, setIsMounted] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [listData, setListData] = useState([]);

    const pagePermission = constants.PERMISSION_SETTINGS_FINES;
    const [hasPermission, setHasPermission] = useState(false);
    const [canEdit, setCanEdit] = useState(false);

    const selectedHandler = (fine) => {
        navigate(`/fines/edit/${fine.id}`);
    };

    const addedHandler = () => {
        navigate(`/fines/new`);
    };

    const refreshedHandler = () => {
        listRecords();
    };

    useEffect(() => {
        if (!hasPermission) { return; }
        if (isMounted) { return; }

        dispatch(breadcrumbAddRoute({ key: 'fines', text: 'Fines', path: window.location.pathname }));
        listRecords();

        setIsMounted(true);
    }, [isMounted, hasPermission, listRecords, dispatch]);

    useEffect(() => {
        if (!_.isEmpty(data)) {
            if (data.status === 'fail' || data.status === 'error') {
                addAlert({ key: constants.ALERT_SERVER_ERROR, text: <strong>{data.body}</strong>, type: constants.ALERT_TYPE_DANGER });
                clearData();
            }

            if (data.scope === 'delete' && data.status === 'success') {
                listRecords();
            }

            if (data.status === 'success' && data.scope === 'list') {
                setListData(data.body);
            }

            setIsBusy(data.status === 'processing' && (data.scope === 'delete' || data.scope === 'list'));
        }
    }, [data, addAlert, clearData, listRecords]);

    useEffect(() => {
        return () => {
            dispatch(breadcrumbRemoveRoute());
        };
    }, [dispatch]);

    return (
        <>
            <SideMenu activeKey="fines" />
            <div className="content-body">
                <Permission showDenied={true} pagePermission={pagePermission} hasPermission={hasPermission} setHasPermission={setHasPermission} setCanEdit={setCanEdit} />
                {hasPermission && isMounted && 
                    <Suspense fallback={<Spinner text="Loading..." />}>
                        <div style={{ paddingRight: '15px' }}>
                            <PageHeader title="Fines" />
                            <Panel>
                                <Panel.Body>
                                    <BaseList
                                        compType="Fine"
                                        listData={listData}
                                        isBusy={isBusy}
                                        sorting={sorting}
                                        columns={columns}
                                        hideCopy={true}
                                        hidePrint={true}
                                        hideFilter={true}
                                        hideDelete={true}
                                        permissionEdit={canEdit}
                                        added={() => addedHandler()}
                                        selected={(fine) => selectedHandler(fine)}
                                        refreshed={() => refreshedHandler()} />
                                </Panel.Body>
                            </Panel>
                        </div>
                    </Suspense>
                }
            </div>
        </>
    );
}

export default withAuthentication(withLayout(Fines));
